import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import createAxiosInstance from "../../../utils/axiosConfig";
import {
	defaultUserSearchColumns,
	userLoggedInColumns,
	userLoginActivityColumns,
	conversationHistoryColumns,
	fmaActivityColumns,
} from "../../../utils/constants";
import AdminTables from "../../common/adminTables";
import Messages from "../../common/messages";
import { ConfirmationModal } from "../../common/modal";
import AdminLayout from "../../common/adminLayout";
import UserFeedback from "./userFeedback";

const UserReporting = () => {
	const navigate = useNavigate();
	const { userData, logout } = useAuth();
	const [formData, setFormData] = useState<{
		results: [];
		total_count: number;
	}>({ results: [], total_count: 0 });
	const [feedbackFormData, setFeedbackFormData] = useState<{
		results: any[];
		total_count: number;
	}>({ results: [], total_count: 0 });
	const [useCase, setUseCase] = useState("chat_feedback"); // New state for use case
	const [currentPage, setCurrentPage] = useState(1); // New state for current page
	const [itemsPerPage, setItemsPerPage] = useState(5); // New state for items per page
	const [isLoading, setIsLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [selectedRowData, setSelectedRowData] = useState<any>(null);
	const [activeFeedbackTab, setActiveFeedbackTab] = useState("positive");
	const [feedbackView, setFeedbackView] = useState<'pending' | 'approved'>('pending');
	const [sortColumn, setSortColumn] = useState<string | null>(null);
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

	const getChatErrors = async () => {
		try {
			const response = await createAxiosInstance().get("/chat_error-logs", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
					sort_by: sortColumn,
					sort_order: sortDirection,
				},
			});

			return response;
		} catch (error: any) {
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getSearchActivity = async () => {
		try {
			const response = await createAxiosInstance().get("/chat/user_search_activity", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
					sort_by: sortColumn,
					sort_order: sortDirection,
				},
			});

			return response;
		} catch (error: any) {
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getLoginActivity = async () => {
		try {
			const response = await createAxiosInstance().get("/login-activity", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
					sort_by: sortColumn,
					sort_order: sortDirection,
				}
			});
			return response;
		} catch (error: any) {
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getActiveUsers = async () => {
		try {
			const response = await createAxiosInstance().get("/logged-in-users", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
					sort_by: sortColumn,
					sort_order: sortDirection,
				}
			});
			return response;
		} catch (error: any) {
			console.log(error, "error");
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getFMAActivity = async () => {
		try {
			const response = await createAxiosInstance().get("/assessment/user_activity", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
					sort_by: sortColumn,
					sort_order: sortDirection,
				}
			});
			return response;
		} catch (error: any) {
			console.log(error, "error");
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getFormData = async () => {
		try {
			setIsLoading(true);
			setFormData({ results: [], total_count: 0 });
			if (useCase === "chat_errors") {
				const response = await getChatErrors();
				setFormData(response.data);
			} else if (useCase === "login_activity") {
				const response = await getLoginActivity();
				setFormData(response.data);
			} else if (useCase === "activeUsers") {
				const response = await getActiveUsers();
				setFormData(response.data);
			} else if (useCase === "search_activity"){
				const response = await getSearchActivity();
				// Filter out entries with empty conversation arrays
				if (response.data && response.data.results) {
					const filteredResults = response.data.results.filter(
						(item: any) => item.conversation && item.conversation.length > 0
					);
					setFormData({
						results: filteredResults,
						total_count: response.data.total_count
					});
				} else {
					setFormData(response.data);
				}
			} else if (useCase === "fma_activity") {
				const response = await getFMAActivity();
				setFormData(response.data);
			}

		} catch (error: any) {
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleRowSelection = (rowData:any)=>{
		console.log(rowData, "rowData");
		
		let updatedRowData = { ...rowData };
		
		// Remove question property from first conversation object
		if (updatedRowData.conversation && updatedRowData.conversation.length > 0) {
			updatedRowData.conversation = updatedRowData.conversation.map((item: any, index: number) => {
				if (index === 0) {
					const { question, ...rest } = item;
					return rest;
				}
				return item;
			});
		}
		
		setSelectedRowData(updatedRowData);
		setShowModal(true);
	}

	const onClose = ()=>{
		setSelectedRowData(null);
		setShowModal(false);
	}

	const handleServerSort = (columnId: string, direction: 'asc' | 'desc' | false) => {
		if (direction === false) {
			// Clear sorting
			setSortColumn(null);
			setSortDirection('desc');
		} else {
			setSortColumn(columnId);
			setSortDirection(direction);
		}
		// Reset to first page when sorting changes
		setCurrentPage(1);
	};

	useEffect(() => {
		getFormData();
	}, [useCase, currentPage, itemsPerPage, sortColumn, sortDirection]);

	useEffect(() => {
		// Allow access for admins and account managers (account_type = 4)
		if (userData && !userData?.is_admin && userData?.account_type !== 4) {
			navigate("/");
		}
	}, [userData]);


	const modalBody = (rowData:any)=>{
		const formattedMessages = rowData?.conversation?.flatMap((msg: any) => [
			// Create question message (right side)
			msg.question ? {
			type: 'right',
			text: msg.question || '',
			time: msg.timestamp || new Date().toLocaleTimeString(),
			icon: false,
			typewriter: false,
			} : null,
			// Create response message (left side)
			msg.response ? {
			type: 'left',
			text: msg.response || '',
			time: msg.timestamp || new Date().toLocaleTimeString(),
			icon: false,
			typewriter: false,
			} : null
		].filter(Boolean)) || []; // Filter out null items

		return (<div className="" style={{
			// marginTop: '18%',
			maxHeight: '58.2vh',
			textAlign: 'left',
			overflowY: 'auto',
			fontSize: '0.875rem',
			// maxHeight: '80vh'
		}}>
			{rowData && <Messages
				useCase={rowData?.conversation[0]?.use_case}
				messages={formattedMessages}
				chat_id={rowData?.id}
				handleOptionSelect={()=>{}}
				handleUseCaseSelect={()=>{}}
				handleSubCase={()=>{}}
				showMessage={false}
				selectedSubCase={
					rowData?.conversation[0]?.label.split('.').length >= 1 ? rowData?.conversation[0]?.label.split('.')[1] : ''
				}
				setDisabled={()=>{}}
				hideIcons={true}
				
			/>}
		</div>);
	}

	// Create action panel component
	const UseCaseSelector = () => (
		<>
			<div className={`d-flex align-items-end ${useCase === 'chat_feedback' ? 'justify-content-between' : 'justify-content-end'}`}>
				{/* Feedback view toggle - only show when chat_feedback is selected */}
				{useCase === 'chat_feedback' && (
					<div className="mb-4 me-4">
						{/* Feedback tabs - only show for pending view */}
						<div className="btn-group" role="group" aria-label="Feedback type filter">
							{[
								{ key: 'pending', label: 'Pending Review', icon: 'fas fa-thumbs-down' },
								{ key: 'approved', label: 'Approved/Global', icon: 'fas fa-thumbs-up' },
								
							].map((tab) => (
								<button
									key={tab.key}
									type="button"
									className={`btn ${activeFeedbackTab === tab.key ? 'btn-primary' : 'btn-outline-secondary'}`}
									onClick={() => {
										setFeedbackView(tab.key as 'approved' | 'pending');
										setCurrentPage(1);

										setActiveFeedbackTab(tab.key);
										// setCurrentPage(1);
									}}
									style={{
										backgroundColor: activeFeedbackTab === tab.key ? '#183FCE' : '#f8f9fa',
										color: activeFeedbackTab === tab.key ? 'white' : 'black',
										borderColor: '#dee2e6',
										fontSize: '14px',
										fontWeight: '500',
										transition: 'all 0.2s ease',
										minWidth: '120px'
									}}
									onMouseEnter={(e) => {
										if (activeFeedbackTab !== tab.key) {
											e.currentTarget.style.backgroundColor = '#e9ecef';
										}
									}}
									onMouseLeave={(e) => {
										if (activeFeedbackTab !== tab.key) {
											e.currentTarget.style.backgroundColor = '#f8f9fa';
										}
									}}
								>
									<i className={`${tab.icon} me-2`}></i>
									{tab.label}
								</button>
							))}
						</div>
					</div>
				)}
				
				<div className=" w-20" style={{ textAlign: 'right' }}>
					<label htmlFor="floatingSelect" style={{ display: 'block', textAlign: 'left' }}>Select a use case:</label>
					<select
						value={useCase}
						onChange={(e) => {
							setUseCase(e.target.value);
							setCurrentPage(1);
							setItemsPerPage(10);
							// Reset sorting when use case changes
							setSortColumn(null);
							setSortDirection('desc');
						}}
						className="form-select bg-white mb-4"
						id="floatingSelect"
						aria-label="Floating label select example"
					>
						<option value="fma_activity">FMA Activity</option>
						<option value="search_activity">
							Search Activity (users)
						</option>
						<option value="chat_errors">Error Logs (chat)</option>
						<option value="login_activity">Login Activity</option>
						<option value="activeUsers">Active Users</option>
						<option value="chat_feedback">Chat Feedback</option>
						
					</select>
				</div>
			</div>
		</>
	);
	
	const getColumnsForUseCase = (useCase: string) => {
		switch (useCase) {
			case "chat_errors":
				return defaultUserSearchColumns;
			case "login_activity":
				return userLoginActivityColumns;
			case "search_activity":
				return conversationHistoryColumns;
			case "fma_activity":
				return fmaActivityColumns;
			default:
				return userLoggedInColumns;
		}
	}
	
	return (
		<AdminLayout
			title="User Reporting"
			actionPanel={<UseCaseSelector />}
			formData={useCase === 'chat_feedback' ? feedbackFormData : formData}
			currentPage={currentPage}
			setCurrentPage={setCurrentPage}
			itemsPerPage={itemsPerPage}
			setItemsPerPage={setItemsPerPage}
		>
			{useCase === 'chat_feedback' ? (
				<UserFeedback
					currentPage={currentPage}
					setCurrentPage={setCurrentPage}
					itemsPerPage={itemsPerPage}
					setItemsPerPage={setItemsPerPage}
					activeTab={activeFeedbackTab}
					setActiveTab={setActiveFeedbackTab}
					feedbackView={feedbackView}
					onDataChange={setFeedbackFormData}
				/>
			) : (
				<AdminTables
					containerStyle={{
						width: "100%",
						overflowX: "auto",
						marginTop: '0.8rem'
					}} 
					data={formData.results}
					columns={getColumnsForUseCase(useCase)}
					rowSelectionEnabled={useCase == 'search_activity'}
					onRowSelect={useCase == 'search_activity' ? handleRowSelection : ()=>{}}
					showSearch={false}
					isLoading={isLoading}
					serverSideSorting={true}
					onServerSort={handleServerSort}
					sortColumn={sortColumn}
					sortDirection={sortDirection}
				/>
			)}
			<ConfirmationModal
				show={showModal}
				onClose={onClose}
				closeText={'Close'}
				title={'Conversation'}
				body={modalBody(selectedRowData)} 
				maxHeight={"80vh"}
				maxWidth={'70vw'}
			/>
		</AdminLayout>
	);
};

export default UserReporting;
